import {
  selectEffectiveRoomTab,
  selectActiveValidationColor,
  selectCeilingCommentCount,
  selectRoomSidebarIcons,
} from "./roomSidebarSelectors";
import { selectValidationViewThunk } from "./validationPanelThunks";
// src/SelectedSpace/RoomSideBar.tsx
import React from "react";
import { ActionIcon, Box, Flex, Text, Tooltip, Transition, SegmentedControl } from "@mantine/core";
import { isValidationPanelTab, validationTabForColor } from "./validationPanelTabs";
import { MdClose } from "react-icons/md";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setSelectedRoomTabInRedux, setSelectedSpaceNumberInRedux } from "../DesignTab/designSlice";
import StandardRoom from "./StandardRoom/StandardRoom";
import RoomCeilingValidations from "./SpaceValidations/RoomCeilingValidations";
import {
  useGetMcbimItemsInSpaceQuery,
  useGetMcbimSingleMcbimSpaceQuery,
  useGetMultiplacerSingleRoomQuery,
} from "../../openapi";
import { selectHttpDevicesInRoom } from "../DesignTab/devices/deviceSlice";
import { selectSpaceValidationForSelectedSpace } from "../DesignTab/designSelector";
import { skipToken } from "@reduxjs/toolkit/query";
import { RoomInfoViewer } from "./RoomInfoViewer";
import { DrofusStandardRoomDiffAllDisciplines } from "./StandardRoomValidation/DrofusStandardRoomDiffAllDisciplines";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import MultiplacerCeilingDevices from "./MultiplacerCeilingDevices/MultiplacerCeilingDevices";
import { selectHttpCurrentProjct } from "../projectSelectors";
import { selectMcBimItemsInSelectedRoomArgs } from "./singleRoomSelectors";
import { DrofusSpecifiedArticlesDiffAllResponsibilities } from "./DrofusSpecifiedArticlesDiffAllResponsibilities";
import { selectGetMcbimStandardSpaceArgs } from "../McBim/httpArgsMcBimSelectors";
import { RoomSectionsPanel } from "../RoomSectionViews/RoomSectionsPanel";
import { RoomNumberingDetailsPanel } from "./RoomNumberingDetailsPanel";
import { CeilingCommentsPanel } from "./CeilingComments/CeilingCommentsPanel";
import { RoomDrofusChangesPanel } from "../DrofusChanges/RoomDrofusChangesPanel";
import { CeilingAiPredictionsPanel } from "./CeilingAiPredictions/CeilingAiPredictionsPanel";

type IRoomSideBarProps = {
  tabMode?: "context" | "all";
};

export const RoomSideBar: React.FC<IRoomSideBarProps> = ({ tabMode = "context" }) => {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectHttpCurrentProjct);
  const selectedRoomTab = useAppSelector(selectEffectiveRoomTab);
  const selectedSpaceNumber = useAppSelector((state) => state.design.selectedSpaceNumber) as string;

  const { data: singleRoom } = useGetMultiplacerSingleRoomQuery(
    project && selectedSpaceNumber
      ? { projectNo: project.projectNo, roomNumber: selectedSpaceNumber }
      : skipToken
  );

  const devicesInRoom = useAppSelector(selectHttpDevicesInRoom);

  const mcbimStandardSpaceArgs = useAppSelector(selectGetMcbimStandardSpaceArgs);

  const { data: mcbimSpaceStandardRoom } = useGetMcbimSingleMcbimSpaceQuery(
    mcbimStandardSpaceArgs ?? skipToken
  );

  const { data: mcbimItemsInStandardSpace = [] } = useGetMcbimItemsInSpaceQuery(
    {
      projectNo: project.projectNo,
      spaceId: mcbimSpaceStandardRoom?._id as string,
    },
    { skip: mcbimSpaceStandardRoom == null }
  );

  useGetMcbimItemsInSpaceQuery(useAppSelector(selectMcBimItemsInSelectedRoomArgs) ?? skipToken);

  const spaceValidations = useAppSelector(selectSpaceValidationForSelectedSpace);

  return (
    <Box
      c="black"
      bg="#e1e1e1b8"
      h="100%"
      w="100%"
      style={{ display: "flex", flexDirection: "column", backdropFilter: "blur(8px)" }}
    >
      <Transition mounted transition="slide-down" duration={200} timingFunction="ease-out">
        {(styles) => (
          <Flex align="center" justify="space-between" px="md" py="xs" bg="blue.0" style={styles}>
            <Text c="black" size="sm" fw={600} lineClamp={1}>
              {singleRoom?.roomNumber}, {singleRoom?.roomName}
            </Text>

            <ActionIcon
              size="sm"
              radius="xl"
              variant="subtle"
              aria-label="Lukk rompanelet"
              onClick={() => dispatch(setSelectedSpaceNumberInRedux(undefined))}
            >
              <MdClose size={16} />
            </ActionIcon>
          </Flex>
        )}
      </Transition>

      <Box
        w="100%"
        style={{ display: "flex", flex: 1, minHeight: 0, justifyContent: "space-between" }}
      >
        <Box h="100%" style={{ flex: 1, minWidth: 0, overflowY: "auto", overflowX: "hidden" }}>
          {selectedRoomTab === RoomSideBarTabs.CEILING_MULTIPLACER_PRODUCTS && (
            <MultiplacerCeilingDevices />
          )}

          {selectedRoomTab === RoomSideBarTabs.CEILING_AI_PREDICTIONS && (
            <CeilingAiPredictionsPanel />
          )}

          {selectedRoomTab === RoomSideBarTabs.ROOM_INFO && singleRoom != null && (
            <RoomInfoViewer />
          )}

          {selectedRoomTab === RoomSideBarTabs.ROOM_SECTION_VIEWS && <RoomSectionsPanel />}

          {selectedRoomTab === RoomSideBarTabs.CEILING_STANDARD_ROOM &&
            singleRoom?.rfpMalRom != null &&
            mcbimSpaceStandardRoom != null && <StandardRoom />}

          {selectedRoomTab === RoomSideBarTabs.CEILING_VALIDATIONS && (
            <RoomCeilingValidations
              spaceValidations={spaceValidations}
              mcbimItemsInStandardSpace={mcbimItemsInStandardSpace}
              devices={devicesInRoom}
            />
          )}
          {selectedRoomTab === RoomSideBarTabs.CEILING_COMMENTS && <CeilingCommentsPanel />}
          {isValidationPanelTab(selectedRoomTab) && (
            <Box
              h="100%"
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                background: "#f4f7fb",
              }}
            >
              <Box p="sm" style={{ borderBottom: "1px solid #dde5ef", flexShrink: 0 }}>
                <Text size="sm" fw={650} mb={6}>
                  Validering
                </Text>
                <SegmentedControl
                  fullWidth
                  size="sm"
                  color="blue"
                  radius="md"
                  aria-label="Type validering"
                  value={selectedRoomTab}
                  onChange={(value) =>
                    dispatch(selectValidationViewThunk(value as RoomSideBarTabs))
                  }
                  data={[
                    {
                      value: RoomSideBarTabs.RFP_STANDARD_ROOM_DIFF,
                      label: (
                        <Tooltip
                          label="Sammenlign rommet med standardrommet, fordelt på disiplin."
                          withArrow
                        >
                          <span>Standardrom</span>
                        </Tooltip>
                      ),
                    },
                    {
                      value: RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES,
                      label: (
                        <Tooltip
                          label="Sammenlign artikler programmert i dRofus med forekomstene i modellen."
                          withArrow
                        >
                          <span>Artikler</span>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Box>
              <Box style={{ flex: 1, minHeight: 0 }}>
                {selectedRoomTab === RoomSideBarTabs.RFP_STANDARD_ROOM_DIFF ? (
                  <DrofusStandardRoomDiffAllDisciplines />
                ) : (
                  <DrofusSpecifiedArticlesDiffAllResponsibilities />
                )}
              </Box>
            </Box>
          )}
          {selectedRoomTab === RoomSideBarTabs.ROOM_NUMBERING_DETAILS && (
            <RoomNumberingDetailsPanel />
          )}
          {selectedRoomTab === RoomSideBarTabs.ROOM_DROFUS_CHANGES && <RoomDrofusChangesPanel />}
        </Box>
        <IconVerticalBar tabMode={tabMode} />
      </Box>
    </Box>
  );
};

const IconVerticalBar: React.FC<{
  tabMode: "context" | "all";
}> = ({ tabMode }) => {
  const dispatch = useAppDispatch();

  const selectedRoomTab = useAppSelector(selectEffectiveRoomTab);
  const activeColor = useAppSelector(selectActiveValidationColor);
  const ceilingCommentCount = useAppSelector(selectCeilingCommentCount);
  const icons = useAppSelector((state) => selectRoomSidebarIcons(state, tabMode));

  return (
    <Box h="100%" bg="gray.2" style={{ flexShrink: 0 }}>
      <ActionIcon.Group orientation="vertical">
        {icons.map((iconItem) => {
          if (iconItem == null) return null;
          const { key, label, icon: IconComp, tooltip, color } = iconItem;

          const active = isValidationPanelTab(key)
            ? isValidationPanelTab(selectedRoomTab)
            : selectedRoomTab === key;
          return (
            <Tooltip label={tooltip} key={key} position="left">
              <ActionIcon
                style={{ outline: "none", position: "relative" }}
                variant={active ? "filled" : "default"}
                size="md"
                aria-label={label}
                color={active ? "blue" : "gray"}
                aria-pressed={active}
                onClick={() => {
                  if (!active)
                    dispatch(
                      setSelectedRoomTabInRedux(
                        isValidationPanelTab(key)
                          ? (validationTabForColor(activeColor) ?? key)
                          : key
                      )
                    );
                }}
              >
                {color == null ? <IconComp /> : <IconComp color={color} />}
                {key === RoomSideBarTabs.CEILING_COMMENTS && ceilingCommentCount > 0 && (
                  <Box
                    pos="absolute"
                    top={0}
                    right={0}
                    bg="orange.5"
                    c="white"
                    px={3}
                    style={{
                      minWidth: 14,
                      height: 14,
                      lineHeight: "14px",
                      fontSize: 10,
                      fontWeight: 700,
                      textAlign: "center",
                      borderRadius: 0,
                      pointerEvents: "none",
                    }}
                  >
                    {ceilingCommentCount > 9 ? "9+" : ceilingCommentCount}
                  </Box>
                )}
              </ActionIcon>
            </Tooltip>
          );
        })}
      </ActionIcon.Group>
    </Box>
  );
};
